// Import the main trigger config schema
import configSchema from '@rematter/frontend-lib-api-client/json-schemas/automations.v1.BusinessEventTriggerConfig.jsonschema.json';
import type { JSONSchema7 } from 'json-schema';

// Source type for the trigger config
export interface BusinessEventSource {
  case?: string;
  value?: unknown;
}

// Type for the config (no longer generated by Zod)
export interface BusinessEventTriggerConfigType {
  filterExpression?: string;
  source?: BusinessEventSource;
  [key: string]: unknown; // Index signature for Record<string, unknown> compatibility
}

/** Entity info for UI display */
export interface EntityInfo {
  key: string;
  label: string;
  module: string;
}

/** Event case info for UI display */
export interface EventCaseInfo {
  key: string;
  label: string;
}

// Map entity keys to their event schema filenames (loaded via import.meta.glob)
const ENTITY_EVENT_SCHEMA_FILES: Record<string, string> = {
  // CRM
  account: 'crm.v1.AccountEvent.jsonschema.json',
  accountAddress: 'crm.v1.AccountAddressEvent.jsonschema.json',
  contact: 'crm.v1.ContactEvent.jsonschema.json',
  customerInvitation: 'crm.v1.CustomerInvitationEvent.jsonschema.json',
  // Trade
  salesOrder: 'trade.v1.SalesOrderEvent.jsonschema.json',
  load: 'trade.v1.LoadEvent.jsonschema.json',
  booking: 'trade.v1.BookingEvent.jsonschema.json',
  purchaseOrder: 'trade.v1.PurchaseOrderEvent.jsonschema.json',
  // Inventory
  item: 'inventory.v1.ItemEvent.jsonschema.json',
  category: 'inventory.v1.CategoryEvent.jsonschema.json',
  itemGroup: 'inventory.v1.ItemGroupEvent.jsonschema.json',
  inventoryTag: 'inventory.v1.InventoryTagEvent.jsonschema.json',
  // Core
  facility: 'core.v1.FacilityEvent.jsonschema.json',
  user: 'core.v1.UserEvent.jsonschema.json',
  organization: 'core.v1.OrganizationEvent.jsonschema.json',
  // Transportation
  driver: 'transportation.v1.DriverEvent.jsonschema.json',
  truck: 'transportation.v1.TruckEvent.jsonschema.json',
  trailer: 'transportation.v1.TrailerEvent.jsonschema.json',
  asset: 'transportation.v1.AssetEvent.jsonschema.json',
  route: 'transportation.v1.RouteEvent.jsonschema.json',
  freightLine: 'transportation.v1.FreightLineEvent.jsonschema.json',
  freightForwarder: 'transportation.v1.FreightForwarderEvent.jsonschema.json',
  containerType: 'transportation.v1.ContainerTypeEvent.jsonschema.json',
  shippingCarrier: 'transportation.v1.ShippingCarrierEvent.jsonschema.json',
  transportationType:
    'transportation.v1.TransportationTypeEvent.jsonschema.json',
  task: 'transportation.v1.TaskEvent.jsonschema.json',
};

// =============================================================================
// JSON Schema Loading (must be before getEntityEventSchema)
// =============================================================================

/** Schema cache for loaded JSON schemas */
type SchemaMap = Record<string, JSONSchema7>;

/** Cache for loaded schemas (for $ref resolution) */
const schemaCache: SchemaMap = {};

/**
 * Use import.meta.glob to eagerly load all json-schemas at build time.
 * This is necessary because Vite can't handle dynamic imports with computed paths.
 * Uses relative path since import.meta.glob requires paths starting with '/' or './'.
 */
const allSchemas = import.meta.glob<{ default: JSONSchema7 }>(
  '../../../../../../../../../frontend-lib-api-client/src/json-schemas/*.jsonschema.json',
  { eager: true },
);

// Populate the schema cache with all loaded schemas
// The key is the schema ID (filename without path)
for (const [path, module] of Object.entries(allSchemas)) {
  // Extract filename from path like "/packages/frontend-lib-api-client/src/json-schemas/crm.v1.Account.jsonschema.json"
  const filename = path.split('/').pop();
  if (filename) {
    schemaCache[filename] = module.default;
  }
}

// Module groupings for UI organization
const ENTITY_MODULES: Record<string, string> = {
  // Core
  facility: 'core',
  user: 'core',
  organization: 'core',
  // Trade
  salesOrder: 'trade',
  load: 'trade',
  booking: 'trade',
  purchaseOrder: 'trade',
  // CRM
  account: 'crm',
  contact: 'crm',
  accountAddress: 'crm',
  customerInvitation: 'crm',
  // Inventory
  item: 'inventory',
  category: 'inventory',
  itemGroup: 'inventory',
  inventoryTag: 'inventory',
  // Transportation
  driver: 'transportation',
  truck: 'transportation',
  trailer: 'transportation',
  asset: 'transportation',
  route: 'transportation',
  freightLine: 'transportation',
  freightForwarder: 'transportation',
  containerType: 'transportation',
  shippingCarrier: 'transportation',
  transportationType: 'transportation',
  task: 'transportation',
};

const MODULE_LABELS: Record<string, string> = {
  core: 'Core',
  trade: 'Trade',
  crm: 'CRM',
  inventory: 'Inventory',
  transportation: 'Transportation',
};

/** Format camelCase to Title Case */
function formatLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// Skip these properties from the config schema (they're not entities)
const NON_ENTITY_PROPERTIES = new Set(['filterExpression']);

/**
 * Get all entity types from the JSON Schema properties
 */
export const getEntities = (): EntityInfo[] => {
  const schema = configSchema as JSONSchema7;
  const schemaProperties = schema.properties ?? {};

  const entityKeys = new Set<string>();
  for (const key of Object.keys(schemaProperties)) {
    if (!NON_ENTITY_PROPERTIES.has(key)) entityKeys.add(key);
  }
  for (const key of Object.keys(ENTITY_MODULES)) {
    entityKeys.add(key);
  }

  const entities: EntityInfo[] = [];
  for (const key of entityKeys) {
    entities.push({
      key,
      label: formatLabel(key),
      module: ENTITY_MODULES[key] ?? 'other',
    });
  }

  return entities;
};

/**
 * Get entities grouped by module for dropdown organization
 * Modules and entities within each module are sorted alphabetically
 */
export const getEntitiesByModule = (): Record<string, EntityInfo[]> => {
  const grouped: Record<string, EntityInfo[]> = {};
  for (const entity of getEntities()) {
    if (!grouped[entity.module]) {
      grouped[entity.module] = [];
    }
    grouped[entity.module].push(entity);
  }

  // Sort modules alphabetically and entities within each module
  const sortedModules = Object.keys(grouped).sort();
  const sorted: Record<string, EntityInfo[]> = {};
  for (const module of sortedModules) {
    sorted[module] = grouped[module].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }
  return sorted;
};

/**
 * Get the JSON Schema for a specific entity's event.
 * Looks up from the schemaCache populated by import.meta.glob.
 */
export function getEntityEventSchema(entityKey: string): JSONSchema7 | null {
  const filename = ENTITY_EVENT_SCHEMA_FILES[entityKey];
  if (!filename) return null;
  // schemaCache is defined later but populated at module load via import.meta.glob
  // By the time this function is called, schemaCache will be populated
  return schemaCache[filename] ?? null;
}

/**
 * Get event cases for a specific entity
 */
export const getEventCasesForEntity = (entityKey: string): EventCaseInfo[] => {
  const eventSchema = getEntityEventSchema(entityKey);
  if (!eventSchema) return [];

  const properties = eventSchema.properties ?? {};
  const cases: EventCaseInfo[] = [];

  for (const key of Object.keys(properties)) {
    cases.push({
      key,
      label: formatLabel(key),
    });
  }

  return cases;
};

/**
 * Get the JSON Schema for a specific entity + event case combination
 */
export const getEventPayloadSchema = (
  entityKey: string,
  eventCase: string,
): JSONSchema7 | null => {
  const eventSchema = getEntityEventSchema(entityKey);
  if (!eventSchema) return null;

  const properties = eventSchema.properties ?? {};
  const eventProperty = properties[eventCase];

  if (!eventProperty || typeof eventProperty === 'boolean') return null;

  // The property has a $ref, but we don't resolve it here
  // The caller can use the $ref to load the full payload schema if needed
  return eventProperty as JSONSchema7;
};

export const defaultBusinessEventTriggerConfig: BusinessEventTriggerConfigType =
  {
    filterExpression: '',
    source: { case: undefined },
  };

export const validateBusinessEventTriggerConfig = (
  config: BusinessEventTriggerConfigType,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!config.source || config.source.case === undefined) {
    errors.source = 'Entity is required';
  }

  return errors;
};

/**
 * Get preview text for the node display
 */
export const getBusinessEventTriggerPreviewText = (
  config: BusinessEventTriggerConfigType,
): string => {
  if (!config.source || config.source.case === undefined) {
    return 'Select entity and event';
  }

  const entityLabel = formatLabel(config.source.case);

  // Try to get the event case from the value
  const sourceValue = config.source.value;
  if (
    sourceValue &&
    typeof sourceValue === 'object' &&
    'event' in sourceValue
  ) {
    const eventObj = sourceValue.event as { case?: string };
    if (eventObj?.case) {
      return `${entityLabel} → ${formatLabel(eventObj.case)}`;
    }
  }

  return entityLabel;
};

export { MODULE_LABELS };

/**
 * Get all business event types as "entity.eventCase" strings
 * Used by BusinessEventTestForm for dropdown options
 */
export const getBusinessEventTypes = (): string[] => {
  const types: string[] = [];
  for (const entity of getEntities()) {
    for (const eventCase of getEventCasesForEntity(entity.key)) {
      types.push(`${entity.key}.${eventCase.key}`);
    }
  }
  return types;
};

/** All business event types as a static array for backwards compatibility */
export const BUSINESS_EVENT_TYPES = getBusinessEventTypes();

/** Type for business event type strings */
export type BusinessEventType = (typeof BUSINESS_EVENT_TYPES)[number];

// =============================================================================
// Additional Schema Loading Utilities
// =============================================================================

/** Cache for entity JSON schemas */
const entitySchemaCache: Map<string, JSONSchema7> = new Map();

/** Map entity keys to their proto package prefixes for schema resolution */
const ENTITY_SCHEMA_PREFIXES: Record<string, string> = {
  // Core
  facility: 'core.v1.Facility',
  user: 'core.v1.User',
  organization: 'core.v1.Organization',
  // CRM
  account: 'crm.v1.Account',
  accountAddress: 'crm.v1.AccountAddress',
  contact: 'crm.v1.Contact',
  customerInvitation: 'crm.v1.CustomerInvitation',
  // Trade
  salesOrder: 'trade.v1.SalesOrder',
  load: 'trade.v1.Load',
  booking: 'trade.v1.Booking',
  purchaseOrder: 'trade.v1.PurchaseOrder',
  // Inventory
  item: 'inventory.v1.Item',
  category: 'inventory.v1.Category',
  itemGroup: 'inventory.v1.ItemGroup',
  inventoryTag: 'inventory.v1.InventoryTag',
  // Transportation
  driver: 'transportation.v1.Driver',
  truck: 'transportation.v1.Truck',
  trailer: 'transportation.v1.Trailer',
  asset: 'transportation.v1.Asset',
  route: 'transportation.v1.Route',
  freightLine: 'transportation.v1.FreightLine',
  freightForwarder: 'transportation.v1.FreightForwarder',
  containerType: 'transportation.v1.ContainerType',
  shippingCarrier: 'transportation.v1.ShippingCarrier',
  transportationType: 'transportation.v1.TransportationType',
  task: 'transportation.v1.Task',
};

/**
 * Get a JSON schema by its ID from the pre-loaded cache.
 * Schemas are loaded eagerly via import.meta.glob at build time.
 */
function loadSchema(schemaId: string): JSONSchema7 | null {
  return schemaCache[schemaId] ?? null;
}

/**
 * Find all $ref values in a schema
 */
function findAllRefs(schema: JSONSchema7, refs: string[] = []): string[] {
  if (typeof schema !== 'object' || schema === null) return refs;

  if (schema.$ref && typeof schema.$ref === 'string') {
    refs.push(schema.$ref);
  }

  if (schema.properties) {
    for (const prop of Object.values(schema.properties)) {
      if (typeof prop === 'object') {
        findAllRefs(prop as JSONSchema7, refs);
      }
    }
  }

  if (
    schema.items &&
    typeof schema.items === 'object' &&
    !Array.isArray(schema.items)
  ) {
    findAllRefs(schema.items as JSONSchema7, refs);
  }

  if (schema.anyOf) {
    for (const opt of schema.anyOf) {
      if (typeof opt === 'object') {
        findAllRefs(opt as JSONSchema7, refs);
      }
    }
  }

  return refs;
}

/**
 * Load all schemas referenced by a root schema (for $ref resolution).
 * All schemas are pre-loaded via import.meta.glob, so this is synchronous.
 */
function loadReferencedSchemas(
  schema: JSONSchema7,
  loaded: Set<string> = new Set(),
): void {
  const refs = findAllRefs(schema);

  for (const ref of refs) {
    if (loaded.has(ref)) continue;
    loaded.add(ref);

    const refSchema = loadSchema(ref);
    if (refSchema && loaded.size < 50) {
      loadReferencedSchemas(refSchema, loaded);
    }
  }
}

/**
 * Get the JSON Schema for business event data (synchronous).
 * Returns the cached schema or undefined if not loaded yet.
 *
 * @param entityKey - Entity key (e.g., "salesOrder")
 * @returns JSONSchema7 | undefined - The entity's JSON Schema
 */
export function getBusinessEventDataSchemaSync(
  entityKey: string,
): JSONSchema7 | undefined {
  return entitySchemaCache.get(entityKey);
}

/**
 * Get the JSON Schema for a business event entity.
 * All schemas are pre-loaded via import.meta.glob, so this is synchronous.
 *
 * @param entityKey - Entity key (e.g., "salesOrder")
 * @returns JSONSchema7 | null
 */
export function loadBusinessEventDataSchema(
  entityKey: string,
): JSONSchema7 | null {
  const cached = entitySchemaCache.get(entityKey);
  if (cached) {
    return cached;
  }

  const schemaPrefix = ENTITY_SCHEMA_PREFIXES[entityKey];
  if (!schemaPrefix) {
    return null;
  }

  const entitySchemaId = `${schemaPrefix}.jsonschema.json`;
  const entitySchema = loadSchema(entitySchemaId);
  if (!entitySchema) {
    return null;
  }

  loadReferencedSchemas(entitySchema);
  entitySchemaCache.set(entityKey, entitySchema);

  return entitySchema;
}

/**
 * Preload business event schema.
 * Since schemas are now pre-loaded via import.meta.glob, this just populates the entity cache.
 */
export function preloadBusinessEventDataSchema(entityKey: string): void {
  loadBusinessEventDataSchema(entityKey);
}

/**
 * Get the schema cache for $ref resolution.
 * Used by Monaco for resolving schema references.
 */
export function getSchemaCache(): SchemaMap {
  return schemaCache;
}
